import type { FieldPath, FieldValues } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  ButtonBase,
  type SxProps,
  type Theme,
  Typography,
  alpha,
} from '@mui/material';

export interface RhfToggleProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  outlined?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

function RhfToggle<T extends FieldValues>({
  name,
  label,
  outlined,
  disabled = false,
  sx,
}: RhfToggleProps<T>) {
  const { control } = useFormContext<T>();
  const { field } = useController({ control, name });
  const isChecked = Boolean(field.value);

  return (
    <ButtonBase
      disabled={disabled}
      onClick={() => field.onChange(!isChecked)}
      sx={[
        {
          width: '100%',
          minHeight: 30,
          px: 1.5,
          justifyContent: 'flex-start',
          borderRadius: 1,
          backgroundColor: isChecked ? 'action.selected' : 'transparent',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          ...(outlined && {
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.text.primary, 0.23),
          }),
        },
        sx,
      ]}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isChecked ? (
          <CheckCircleIcon color="primary" fontSize="small" />
        ) : (
          <RadioButtonUncheckedIcon color="action" fontSize="small" />
        )}
        <Typography variant="body2">{label}</Typography>
      </Box>
    </ButtonBase>
  );
}

export default RhfToggle;
